import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/authService'; 
import { Loader2 } from 'lucide-react';

// ۱. ایجاد Context
const AuthContext = createContext(undefined); 

// --- هوک اختصاصی برای دسترسی راحت ---
export const useAuthContext = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuthContext باید درون یک AuthProvider استفاده شود');
    }
    return context;
};

// --- کامپوننت اصلی Provider ---
export function AuthProvider({ children }) {
    
    const [user, setUser] = useState(null); 
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true); 
    
    // کلید برای اجبار کامپوننت‌های دیگر به بروزرسانی (مثلاً لیست سفارشات یا پروفایل)
    const [profileUpdateKey, setProfileUpdateKey] = useState(0); 

    // ----------------------------------------------------------------
    // ۲. بررسی وضعیت ورود در هنگام اولین لود برنامه
    // ----------------------------------------------------------------
    useEffect(() => {
        const initializeAuth = () => {
            try {
                const token = authService.getAuthTokens(); 
                const storedUser = authService.getUserInfo();
                
                if (token && storedUser) { 
                    setIsLoggedIn(true);
                    setUser(storedUser); 
                } else {
                    // اگر یکی از این دو نبود، برای اطمینان همه‌چیز را پاک می‌کنیم
                    authService.clearAuthData();
                    setIsLoggedIn(false);
                    setUser(null);
                }
            } catch (err) {
                console.error("خطا در بازیابی وضعیت نشست کاربری:", err);
                authService.clearAuthData();
            } finally {
                setIsLoading(false); 
            }
        };
        
        initializeAuth();
    }, []); 

    // ----------------------------------------------------------------
    // ✅ توابع عملیاتی (بروزرسانی شده)
    // ----------------------------------------------------------------

    const triggerProfileUpdate = useCallback(() => {
        setProfileUpdateKey(prev => prev + 1);
    }, []);

    const closeAuthModal = useCallback(() => {
        setIsModalOpen(false);
    }, []); 

    const openAuthModal = useCallback(() => {
        if (!isLoggedIn) {
            setIsModalOpen(true);
        }
    }, [isLoggedIn]); 

    // متد لاگین با پشتیبانی از "مرا به خاطر بسپار"
// در AuthContext.js
    const login = useCallback(async (identifier, password, rememberMe = false) => {
    // فراخوانی سرویس با همان ۳ پارامتر
    const data = await authService.loginUser(identifier, password, rememberMe);
    
    // چون در views.py فیلد user_id برمی‌گردانید:
    setUser({ id: data.user_id, identifier: data.identifier });
    setIsLoggedIn(true);
    triggerProfileUpdate(); 
    closeAuthModal();
    return data;
    }, [closeAuthModal, triggerProfileUpdate]);
    
    const register = useCallback(async (phone, email, password, fullName) => {
    try {
        // ۱. اطمینان از ارسال به سرویس با همان ترتیبی که در authService تعریف شده
        const response = await authService.registerUser(
            phone, 
            email, 
            password, 
            fullName // نام کامل که از مودال می‌آید
        );
        return response;
    } catch (error) {
        // ۲. خطا را به مودال پرتاب می‌کنیم تا توسط handleAxiosAuthError نمایش داده شود
        throw error;
    }
    }, []);

    const handleLogout = useCallback(() => {
        authService.logout(); // این متد خودش clearAuthData را صدا می‌زند
        setIsLoggedIn(false);
        setUser(null);
        triggerProfileUpdate(); 
    }, [triggerProfileUpdate]); 

    // مقادیر ارسالی به کل برنامه
    const value = {
        user, 
        isLoggedIn,
        isModalOpen,
        isLoading,
        openAuthModal,
        closeAuthModal,
        login, 
        register, 
        handleLogout,
        profileUpdateKey, 
        triggerProfileUpdate, 
        getAuthTokens: authService.getAuthTokens, 
    };

    // ----------------------------------------------------------------
    // 🛑 لایه محافظ لودینگ: تا زمانی که وضعیت توکن مشخص نشود، برنامه رندر نمی‌شود
    // ----------------------------------------------------------------
    if (isLoading) {
        return (
            <div className="fixed inset-0 flex flex-col items-center justify-center bg-white z-[100]">
                 <Loader2 className="w-12 h-12 animate-spin text-purple-700 mb-4" />
                 <span className="text-gray-600 font-medium">در حال بررسی دسترسی...</span>
            </div>
        );
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}